import React, { useState, useEffect } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { CategoriaTorneio, deletarCategoriaTorneio } from '../../../services/categoriaTorneioService';
import { InscricoesPorTorneioData } from '../../../services/torneioService';
import { FilaEspera } from '../../../services/filaEsperaService';
import { StatusInscricao } from '../../../types/inscricao';
import {
  formatDataCategoria as formatDate,
  formatModeloDisputa,
} from '../../../utils/categoriaTorneioLabels';
import {
  buscarFaseGrupoPartidasPorFaseCategoriaTorneio,
  FaseGrupoPartidasLite,
  EquipeLite,
} from '../../../services/faseGrupoPartidasService';
import {
  buscarClassificacaoPorGrupo,
  LinhaClassificacaoFront,
} from '../../../services/classificacaoService';
import { listarEquipesDoGrupo } from '../../../services/faseGrupoPartidaEquipeService';
import TeamsTable from './components/TeamsTable';
import GerarPartidasButton from './components/GerarPartidasButton';

interface CategoryAboutTabProps {
  categoriaId: string;
  categoria: CategoriaTorneio;
  inscricoes: InscricoesPorTorneioData['inscricoesTorneioPorAtletaIdTorneioId'];
  filasEspera: FilaEspera[];
  torneioId: string;
  navigate: NavigateFunction;
  errorFilas?: Error | null;
  onToggleRegistration?: () => void;
  isToggleDisabled?: boolean;
  updatingStatus?: boolean;
  toggleButtonText?: string;
  onRefreshCategory?: () => void;
}

const CategoryAboutTab: React.FC<CategoryAboutTabProps> = ({
  categoriaId,
  categoria,
  inscricoes,
  filasEspera,
  torneioId,
  navigate,
  errorFilas,
  onToggleRegistration,
  isToggleDisabled,
  updatingStatus,
  toggleButtonText,
  onRefreshCategory,
}) => {
  // Filtrar inscrições da categoria
  const inscricoesCategoria = inscricoes.filter(
    (i) => i.categoriaTorneio?.id === categoriaId
  );

  // Calcular estatísticas de inscritos (somente confirmadas e pendentes)
  const inscricoesConfirmadas = inscricoesCategoria.filter(
    (i) => i.status === StatusInscricao.CONFIRMADA
  ).length;
  const inscricoesPendentes = inscricoesCategoria.filter(
    (i) => i.status === StatusInscricao.PENDENTE
  ).length;
  const totalInscritos = inscricoesConfirmadas + inscricoesPendentes;
  const vagasDisponiveis = categoria.vagas != null ? categoria.vagas - totalInscritos : null;

  const inscritosFilaEspera = filasEspera.filter(
    (f) => !f.dataConclusao && f.inscricao.categoriaTorneio?.id === categoriaId
  ).length;

  // Calcular estatísticas de inscrições por status (confirmadas x demais status) — não distingue
  // pagas de isentas, ambas contam como confirmadas aqui.
  const inscricoesPagas = inscricoesCategoria.filter(
    (i) => i.status === 'CONFIRMADA'
  ).length;
  const inscricoesNaoPagas = inscricoesCategoria.filter(
    (i) => i.status !== 'CONFIRMADA'
  ).length;

  // Calcular estatísticas de partidas
  // Verificar se fasesCategoria existe e tem dados
  const fasesCategoria = categoria.fasesCategoria || [];
  const todasPartidas = fasesCategoria.flatMap((f) => f.partidas || []);
  const partidasAtivas = todasPartidas.filter((p) => p.status !== 'CANCELADA');
  const totalPartidas = partidasAtivas.length;
  const partidasConcluidas = partidasAtivas.filter(
    (p) => p.placar?.resultadoFinalA != null && p.placar?.resultadoFinalB != null
  ).length;

  const [showErrorFilas, setShowErrorFilas] = useState(true);

  // Classificação: buscar grupos e linhas de classificação da fase GRUPO
  const [gruposComClassificacao, setGruposComClassificacao] = useState<
    Array<{
      grupo: FaseGrupoPartidasLite;
      equipes: EquipeLite[];
      linhas: LinhaClassificacaoFront[];
    }>
  >([]);

  useEffect(() => {
    const faseGrupo = categoria.fasesCategoria.find(
      (f) => f.tipoGrupoChave === 'GRUPO',
    );
    if (!faseGrupo) {
      setGruposComClassificacao([]);
      return;
    }

    let cancelled = false;
    const fetchClassificacao = async () => {
      try {
        const grupos = await buscarFaseGrupoPartidasPorFaseCategoriaTorneio(faseGrupo.id);
        if (cancelled) return;
        const enriched = await Promise.all(
          grupos.map(async (grupo) => {
            const [linhas, vinculos] = await Promise.all([
              buscarClassificacaoPorGrupo(grupo.id),
              listarEquipesDoGrupo(grupo.id),
            ]);
            const equipes: EquipeLite[] = (vinculos ?? [])
              .map((v: any) => v?.equipe)
              .filter((e: EquipeLite | null | undefined): e is EquipeLite => !!e);
            return { grupo, equipes, linhas };
          }),
        );
        if (!cancelled) {
          setGruposComClassificacao(enriched);
        }
      } catch (e) {
        console.error('[CategoryAboutTab] classificacao fetch failed:', e);
      }
    };

    fetchClassificacao();
    return () => {
      cancelled = true;
    };
  }, [categoriaId, categoria.fasesCategoria]);

  const handleDeleteCategory = async () => {
    if (!window.confirm('Tem certeza que deseja excluir esta categoria?')) return;
    try {
      const removed = await deletarCategoriaTorneio(categoriaId);
      if (!removed) {
        window.alert('Não foi possível excluir a categoria. Tente novamente.');
        return;
      }
      window.alert('Categoria excluída com sucesso!');
      onRefreshCategory?.();
      navigate(`/liga/tournament/${torneioId}`);
    } catch {
      window.alert('Não foi possível excluir a categoria. Tente novamente.');
    }
  };

  return (
    <div className="mt-4">
      {/* Alerta de erro para filas de espera (não crítico) */}
      {errorFilas && showErrorFilas && (
        <div className="alert alert-warning alert-dismissible fade show mb-3" role="alert">
          <strong>Aviso:</strong> Não foi possível carregar as informações de fila de espera. As
          estatísticas podem estar incompletas.
          <button
            type="button"
            className="btn-close"
            aria-label="Fechar"
            onClick={() => setShowErrorFilas(false)}
          ></button>
        </div>
      )}

      {/* Cards de Estatísticas */}
      <div className="row mb-4">
        {/* Card de Inscritos */}
        <div className="col-md-4 mb-3">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <h5 className="card-title text-muted mb-2">Inscritos</h5>
              <h2 className="fw-700 mb-1">{totalInscritos}</h2>
              <p className="text-muted mb-0 small">
                {inscricoesConfirmadas} confirmadas, {inscricoesPendentes} pendentes
              </p>
              {vagasDisponiveis != null && (
                <p className="text-muted mb-0 small">
                  {vagasDisponiveis > 0
                    ? `${vagasDisponiveis} vagas restantes`
                    : 'Sem vagas disponíveis'}
                </p>
              )}
              {inscritosFilaEspera > 0 && (
                <p className="text-muted mb-0 small">
                  {inscritosFilaEspera} em fila de espera
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Card de Inscrições */}
        <div className="col-md-4 mb-3">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <h5 className="card-title text-muted mb-2">Inscrições</h5>
              <h2 className="fw-700 mb-1">{totalInscritos}</h2>
              <p className="text-muted mb-0 small">
                {inscricoesPagas} pagas, {inscricoesNaoPagas} não pagas
              </p>
            </div>
          </div>
        </div>

        {/* Card de Partidas */}
        <div className="col-md-4 mb-3">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <h5 className="card-title text-muted mb-2">Partidas</h5>
              <h2 className="fw-700 mb-1">{totalPartidas}</h2>
              <p className="text-muted mb-0 small">
                {partidasConcluidas} concluídas / {totalPartidas} total
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Seção de Classificação — visível na aba "Sobre" para acesso direto sem troca de aba */}
      {gruposComClassificacao.length > 0 && (
        <div className="card shadow-sm border-0 mb-4">
          <div className="card-body p-4">
            <h4 className="fw-700 mb-3">Classificação</h4>
            {gruposComClassificacao.map(({ grupo, equipes, linhas }) => (
              <div key={grupo.id} className="mb-4">
                {gruposComClassificacao.length > 1 && (
                  <h6 className="mb-2 text-muted">{grupo.nome || 'Grupo'}</h6>
                )}
                <TeamsTable equipes={equipes} linhasClassificacao={linhas} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Seção de Informações Importantes */}
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body p-4">
          <h4 className="fw-700 mb-3">Informações Importantes</h4>
          <div className="row">
            <div className="col-md-6 mb-3">
              <strong>Modelo de Disputa:</strong>
              <p className="text-muted mb-0">
                {formatModeloDisputa(categoria.modeloDisputa)}
              </p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Período de Inscrições:</strong>
              <p className="text-muted mb-0">
                {categoria.dataInicioInscricao && categoria.dataFimInscricao
                  ? `${formatDate(categoria.dataInicioInscricao)} até ${formatDate(categoria.dataFimInscricao)}`
                  : 'Não definido'}
              </p>
            </div>
            <div className="col-md-6 mb-3">
              <strong>Período da Categoria:</strong>
              <p className="text-muted mb-0">
                {categoria.dataInicio && categoria.dataFim
                  ? `${formatDate(categoria.dataInicio)} até ${formatDate(categoria.dataFim)}`
                  : 'Não definido'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Seção de Atalhos */}
      <div className="card shadow-sm border-0">
        <div className="card-body p-4">
          <h4 className="fw-700 mb-3">Atalhos</h4>
          <div className="row g-3">
            <div className="col-md-6 col-lg-4">
              <button
                className="btn btn-outline-primary w-100"
                onClick={() => {
                  navigate(`/liga/tournament/${torneioId}/category/${categoriaId}/inscricao/nova`);
                }}
              >
                Adicionar Inscrição
              </button>
            </div>
            <div className="col-md-6 col-lg-4">
              <button
                className="btn btn-outline-primary w-100"
                onClick={onToggleRegistration}
                disabled={isToggleDisabled || updatingStatus}
              >
                {updatingStatus ? 'Atualizando...' : toggleButtonText || 'Abrir/Encerrar Inscrições'}
              </button>
            </div>
            <div className="col-md-6 col-lg-4">
              <GerarPartidasButton
                categoriaId={categoriaId}
                categoria={categoria}
                inscricoesPagas={inscricoesPagas}
                onRefresh={onRefreshCategory}
              />
            </div>
            <div className="col-md-6 col-lg-4">
              <button
                className="btn btn-outline-primary w-100"
                onClick={() => {
                  navigate(`/liga/tournament/${torneioId}/category/${categoriaId}`);
                }}
              >
                Editar Categoria
              </button>
            </div>
            <div className="col-md-6 col-lg-4">
              <button
                className="btn btn-outline-danger w-100"
                onClick={handleDeleteCategory}
              >
                Excluir Categoria
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryAboutTab;

